import { useEffect, useState } from 'react';

import { de, fill } from '../../i18n/de';
import { ContentBand } from '../../components/ContentBand';
import {
  CATEGORIES_PER_SCREEN,
  HIDDEN_IN_SAETZE,
  PHRASES_PER_CATEGORY,
  canPlacePhrase,
  hasRoomForCategory,
  shownInSaetze,
} from '../../core/authoring/capacity';
import { checkAbbrev, type AbbrevProblem } from '../../core/authoring/abbrev';
import { moveOneStep, nextOrder } from '../../core/authoring/order';
import { SEED_KERNVOKABULAR } from '../../data/seed/content';
import { useProfileStore } from '../../core/profile/ProfileStore';
import { gateFor, isValidNewPin, pinMatches, type CareAction } from '../../core/profile/pinGate';
import { abbrevRepo, categoryRepo, metaRepo, phraseRepo } from '../../data/repositories';
import { buildExport, serialiseExport } from '../../data/export';
import { fileExportProvider } from '../../core/sync/fileExport';
import { azureBlobProvider } from '../../core/backup/azureBlob';
import { restoreFrom } from '../../core/backup/restore';
import { shouldEscalate, useBackup } from '../../core/backup/BackupService';
import { usePiperAssets } from '../../core/speech/piper/assets';
import { usePiper } from '../../core/speech/piper/usePiper';
import { adoptDeviceToken, backupCredentials } from '../../core/backup/credentials';
import { audioTransitions } from '../../core/speech/audioUnlock';
import { useAudioStatus } from '../../core/speech/audioStatusStore';
import { warmedAgoMs } from '../../core/speech/warmup';
import { diagnosticsRepo } from '../../data/repositories';
import { missingAudioCount, prerenderMissing } from '../../core/speech/prerender';
import { speechService } from '../../core/speech/SpeechService';
import { useUpdateStore } from '../../app/serviceWorker';
import { useUiStore } from '../../app/uiStore';
import { BackupSecrets } from './BackupSecrets';
import { CalibrationPanel } from './CalibrationPanel';
import { LearningPanel } from './LearningPanel';
import { SettingsPanel } from './SettingsPanel';
import type { SyncVersion } from '../../core/sync/SyncProvider';
import type { Abbrev, Category, Meta, Phrase } from '../../core/types';

/**
 * Betreuer-Modus (1.13).
 *
 * The one rule that shapes it: **only destructive actions are gated** (non-negotiable §3.10). A
 * caregiver has to be able to add a phrase at 3 a.m. because the person needs a word tonight, and a
 * PIN in front of that would make care mode something you avoid. Deleting is a different matter, and
 * so is importing a backup — which looks restorative and can discard everything.
 *
 * Everything sits inside the content band: this is dense, search-driven UI, and it is the one screen
 * where the *caregiver* is the reader.
 */

type Section = 'phrases' | 'abbrevs' | 'settings' | 'audio' | 'backup' | 'diagnostics' | 'learning';

export function BetreuerModus({ onExit }: { onExit: () => void }): React.JSX.Element {
  // Whoever sent the caregiver here may have had a destination in mind. Only the first-launch restore offer
  // does, and asking somebody to find the right tab would turn a one-tap offer into a hunt.
  const [section, setSection] = useState<Section>(
    () => (useUiStore.getState().takeCareSection() as Section | null) ?? 'settings',
  );
  const settings = useProfileStore((s) => s.settings);
  const [pending, setPending] = useState<{ action: CareAction; run: () => void } | null>(null);
  const [pinEntry, setPinEntry] = useState('');
  const [pinError, setPinError] = useState(false);

  /**
   * The single gate. Every destructive call site goes through here rather than remembering to check,
   * so "which actions are protected" is one enumerated decision (`pinGate.ts`) instead of a habit.
   */
  const guard = (action: CareAction, run: () => void): void => {
    const gate = gateFor(action, settings.pin);
    if (gate.kind === 'allow') {
      run();
      return;
    }
    setPinEntry('');
    setPinError(false);
    setPending({ action, run });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, overflowY: 'auto', background: 'var(--bg)' }}>
      <ContentBand>
        <div style={{ paddingBlock: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <header style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h1 style={{ flex: '1 1 auto', fontSize: 'var(--font-button)' }}>
              {de.surfaceBetreuerModus}
            </h1>
            <CareButton id="care:exit" label={de.careExit} onPress={onExit} />
          </header>

          {settings.pin === null ? (
            <p
              data-testid="care-pin-warning"
              style={{
                padding: 8,
                fontSize: 'var(--font-body)',
                border: '3px dashed var(--danger)',
                borderRadius: 'var(--radius)',
              }}
            >
              {de.carePinPrompt}
            </p>
          ) : null}

          <nav style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {(
              [
                ['settings', de.careSectionSettings],
                ['phrases', de.careSectionPhrases],
                ['abbrevs', de.careSectionAbbrevs],
                ['audio', de.careSectionAudio],
                ['backup', de.careSectionBackup],
                ['learning', de.careSectionLearning],
                ['diagnostics', de.careSectionDiagnostics],
              ] as Array<[Section, string]>
            ).map(([id, label]) => (
              <CareButton
                key={id}
                id={`care-tab:${id}`}
                label={label}
                onPress={() => setSection(id)}
                active={section === id}
              />
            ))}
          </nav>

          {section === 'settings' ? <SettingsPanel guard={guard} /> : null}
          {section === 'phrases' ? <PhrasesPanel guard={guard} /> : null}
          {section === 'abbrevs' ? <AbbrevPanel guard={guard} /> : null}
          {section === 'audio' ? <AudioPanel /> : null}
          {section === 'backup' ? <BackupPanel guard={guard} /> : null}
          {section === 'learning' ? <LearningPanel /> : null}
          {section === 'diagnostics' ? <DiagnosticsPanel /> : null}

          <CalibrationPanel />
        </div>
      </ContentBand>

      {pending ? (
        <div
          data-testid="care-gate"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: 'min(70%, 520px)',
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              background: 'var(--bg)',
              color: 'var(--fg)',
              border: '4px solid var(--danger)',
              borderRadius: 'var(--radius)',
            }}
          >
            <p style={{ fontSize: 'var(--font-button)' }}>
              {settings.pin === null ? de.confirmDelete : de.pinRequired}
            </p>

            {settings.pin === null ? null : (
              <input
                data-testid="care-pin-input"
                type="password"
                inputMode="numeric"
                value={pinEntry}
                onChange={(event) => {
                  setPinEntry(event.target.value);
                  setPinError(false);
                }}
                style={{
                  minHeight: 'var(--min-target)',
                  fontSize: 'var(--font-button)',
                  padding: 8,
                  border: '3px solid var(--key-border)',
                  borderRadius: 'var(--radius)',
                }}
              />
            )}

            {pinError ? <p style={{ color: 'var(--danger)' }}>{de.pinWrong}</p> : null}

            <div style={{ display: 'flex', gap: 8 }}>
              <CareButton
                id="care-gate:confirm"
                label={de.confirm}
                danger
                onPress={() => {
                  if (!pinMatches(pinEntry, settings.pin)) {
                    setPinError(true);
                    return;
                  }
                  pending.run();
                  setPending(null);
                }}
              />
              <CareButton id="care-gate:cancel" label={de.cancel} onPress={() => setPending(null)} />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function CareButton({
  id,
  label,
  onPress,
  active = false,
  danger = false,
  disabled = false,
  note,
}: {
  id: string;
  label: string;
  onPress: () => void;
  active?: boolean;
  danger?: boolean;
  disabled?: boolean;
  note?: string;
}): React.JSX.Element {
  return (
    <button
      type="button"
      data-testid={id}
      onClick={onPress}
      disabled={disabled}
      aria-pressed={active}
      style={{
        minHeight: 'var(--min-target)',
        paddingInline: 12,
        fontSize: 'var(--font-body)',
        fontWeight: active ? 700 : 400,
        background: danger ? 'var(--danger)' : active ? 'var(--fg)' : 'var(--key-bg)',
        color: danger ? 'var(--danger-fg)' : active ? 'var(--bg)' : 'var(--key-fg)',
        border: `3px ${danger ? 'dashed' : 'solid'} ${danger ? 'var(--danger)' : 'var(--key-border)'}`,
        borderRadius: 'var(--radius)',
      }}
    >
      {label}
      {note ? <span style={{ opacity: 0.7 }}> ({note})</span> : null}
    </button>
  );
}

/** Adding and editing are ungated; deleting is not. That asymmetry is the whole point. */
/**
 * The German for "this will not fit", or `null` when it will (`AUT-08`, ADR-040).
 *
 * The count is the phrases already in that category and the number in the sentence comes from the
 * grid, so a caregiver is told *which* category and *how many* it holds rather than only "voll".
 *
 * Notfall is exempt: it renders its own tabbed surface rather than the 3 × 3 grid, so the grid's
 * capacity is not its capacity and quoting it would be a false statement about that screen.
 */
function phraseCapacityRefusal(
  categories: readonly Category[],
  phrases: readonly Phrase[],
  categoryId: string,
  movingId?: string,
): string | null {
  if (categoryId === HIDDEN_IN_SAETZE) return null;
  if (canPlacePhrase(phrases, categoryId, movingId)) return null;

  const name = categories.find((category) => category.id === categoryId)?.name ?? categoryId;
  return fill(de.categoryFull, { name, max: PHRASES_PER_CATEGORY });
}

/** The German for "no room for another category", or `null` when there is (`AUT-08`). */
function categoryCapacityRefusal(categories: readonly Category[]): string | null {
  if (hasRoomForCategory(shownInSaetze(categories).length)) return null;
  return fill(de.noRoomForCategory, { max: CATEGORIES_PER_SCREEN });
}

/**
 * What the colour picker *shows* before a caregiver has chosen — no longer what a new category gets.
 *
 * Its old docblock asked the right question and answered it wrongly: *"an unset one has to be legible rather
 * than distinctive"* is true, but legible under **which** theme? This is a literal rather than a theme token,
 * so it looks the same under all four — including `yellowOnBlack`, which this person sees best, and
 * `highContrastBlue`, whose `--key-bg` is `#002d9c` and against which slate grey nearly vanishes.
 *
 * So a new category now starts with **no colour** and takes the theme's border (`AUT-11`, ADR-045). This value
 * survives only as the swatch an `<input type="color">` has to display, since that control cannot represent an
 * absence — which is why the checkbox beside it exists.
 */
const NEW_CATEGORY_COLOUR = '#607d8b';

/** What the editor is holding while it is open. Empty strings, so the inputs are never uncontrolled. */
interface PhraseDraft {
  id: string;
  text: string;
  shortLabel: string;
  categoryId: string;
  pinned: boolean;
}

const draftOf = (phrase: Phrase): PhraseDraft => ({
  id: phrase.id,
  text: phrase.text,
  shortLabel: phrase.shortLabel ?? '',
  categoryId: phrase.categoryId,
  pinned: phrase.pinned,
});

/**
 * The same, for a category's name, colour and icon (`AUT-01`).
 *
 * `colour` and `noColour` are separate because `<input type="color">` always has a value: the checkbox carries
 * the absence and the input keeps whatever swatch it was showing, so ticking and unticking does not lose the
 * colour a caregiver had picked before they changed their mind.
 */
interface CategoryDraft {
  id: string;
  name: string;
  colour: string;
  noColour: boolean;
  icon: string;
}

const categoryDraftOf = (category: Category): CategoryDraft => ({
  id: category.id,
  name: category.name,
  colour: category.colour ?? NEW_CATEGORY_COLOUR,
  noColour: category.colour === undefined,
  icon: category.icon ?? '',
});

/** One category's phrases, in the order the arrows work on: `order` alone, pinning shown not applied. */
function inCategory(phrases: readonly Phrase[], categoryId: string): Phrase[] {
  return phrases
    .filter((phrase) => phrase.categoryId === categoryId)
    .sort((a, b) => a.order - b.order);
}

function PhrasesPanel({
  guard,
}: {
  guard: (action: CareAction, run: () => void) => void;
}): React.JSX.Element {
  const [phrases, setPhrases] = useState<Phrase[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [text, setText] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  /** The phrase being edited, or `null`. One at a time: two open editors is two drafts to lose. */
  const [draft, setDraft] = useState<PhraseDraft | null>(null);
  const [categoryDraft, setCategoryDraft] = useState<CategoryDraft | null>(null);
  const [newCategory, setNewCategory] = useState({
    name: '',
    colour: NEW_CATEGORY_COLOUR,
    // Unset by default (`AUT-11`): the theme's border reads correctly under every theme, and a literal does not.
    noColour: true,
    icon: '',
  });

  const reload = (): void => {
    void phraseRepo.all().then(setPhrases);
    void categoryRepo.all().then((all) => {
      setCategories(all);
      setCategoryId((previous) => previous || (all[0]?.id ?? ''));
    });
  };

  useEffect(reload, []);

  /**
   * One step up or down, written only where it changes something (`AUT-02`).
   *
   * The same function serves phrases and categories because it is the same question, and the rows it
   * is given are always one category's worth — `moveOneStep` never sees the rest of the board.
   */
  const applyMove = <T extends { id: string; order: number }>(
    rows: readonly T[],
    id: string,
    direction: 'up' | 'down',
    write: (row: T) => Promise<void>,
  ): void => {
    const writes = moveOneStep(rows, id, direction);
    if (writes.length === 0) return;

    void Promise.all(
      writes.map((entry) => {
        const row = rows.find((candidate) => candidate.id === entry.id);
        return row === undefined ? Promise.resolve() : write({ ...row, order: entry.order });
      }),
    ).then(reload);
  };

  /**
   * Save the open editor (`AUT-04`).
   *
   * A phrase arriving in a different category takes the place at the end of it, because its old
   * `order` means nothing among its new siblings. Staying put keeps the place the caregiver gave it.
   */
  const saveDraft = (): void => {
    if (draft === null || draft.text.trim() === '') return;

    const original = phrases.find((phrase) => phrase.id === draft.id);
    if (original === undefined) return;

    const full = phraseCapacityRefusal(categories, phrases, draft.categoryId, draft.id);
    if (full !== null) {
      setRefusal(full);
      return;
    }
    setRefusal(null);

    const moved = draft.categoryId !== original.categoryId;
    const next: Phrase = {
      ...original,
      text: draft.text.trim(),
      categoryId: draft.categoryId,
      pinned: draft.pinned,
      order: moved ? nextOrder(inCategory(phrases, draft.categoryId)) : original.order,
    };

    const label = draft.shortLabel.trim();
    if (label === '') delete next.shortLabel;
    else next.shortLabel = label;

    void phraseRepo.upsert(next).then(() => {
      setDraft(null);
      reload();
    });
  };

  /** Create a category, if the phrase screen has a slot to show it in (`AUT-01`, `AUT-08`). */
  const addCategory = (): void => {
    const name = newCategory.name.trim();
    if (name === '') return;

    const full = categoryCapacityRefusal(categories);
    if (full !== null) {
      setRefusal(full);
      return;
    }
    setRefusal(null);

    const category: Category = {
      id: `cat-custom-${Date.now()}`,
      name,
      order: nextOrder(categories),
    };
    // Omitted rather than set to a sentinel, exactly as `icon` is handled below (`AUT-11`, ADR-045).
    if (!newCategory.noColour) category.colour = newCategory.colour;
    const icon = newCategory.icon.trim();
    if (icon !== '') category.icon = icon;

    void categoryRepo.upsert(category).then(() => {
      setNewCategory({ name: '', colour: NEW_CATEGORY_COLOUR, noColour: true, icon: '' });
      reload();
    });
  };

  const saveCategoryDraft = (): void => {
    if (categoryDraft === null || categoryDraft.name.trim() === '') return;

    const original = categories.find((category) => category.id === categoryDraft.id);
    if (original === undefined) return;

    const next: Category = {
      ...original,
      name: categoryDraft.name.trim(),
    };
    if (categoryDraft.noColour) delete next.colour;
    else next.colour = categoryDraft.colour;
    const icon = categoryDraft.icon.trim();
    if (icon === '') delete next.icon;
    else next.icon = icon;

    void categoryRepo.upsert(next).then(() => {
      setCategoryDraft(null);
      reload();
    });
  };

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          data-testid="care-phrase-text"
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={de.addPhrase}
          style={{
            flex: '1 1 240px',
            minHeight: 'var(--min-target)',
            fontSize: 'var(--font-body)',
            padding: 8,
            border: '3px solid var(--key-border)',
            borderRadius: 'var(--radius)',
          }}
        />
        <select
          data-testid="care-phrase-category"
          value={categoryId}
          onChange={(event) => setCategoryId(event.target.value)}
          style={{ minHeight: 'var(--min-target)', fontSize: 'var(--font-body)' }}
        >
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
        {/* No gate: capturing a word mid-conversation must never wait on a PIN. */}
        <CareButton
          id="care:add-phrase"
          label={de.addPhrase}
          onPress={() => {
            if (text.trim() === '' || categoryId === '') return;

            // AUT-08. The refusal keeps the text in the field: a caregiver who has just typed a
            // sentence should be able to pick another category rather than type it again.
            const full = phraseCapacityRefusal(categories, phrases, categoryId);
            if (full !== null) {
              setRefusal(full);
              return;
            }
            setRefusal(null);

            void phraseRepo
              .upsert({
                id: `ph-custom-${Date.now()}`,
                categoryId,
                text: text.trim(),
                order: nextOrder(phrases.filter((phrase) => phrase.categoryId === categoryId)),
                pinned: false,
                useCount: 0,
              })
              .then(() => {
                setText('');
                reload();
              });
          }}
        />
      </div>

      {refusal === null ? null : (
        <p
          data-testid="care-capacity-refusal"
          style={{
            padding: 8,
            fontSize: 'var(--font-body)',
            border: '3px dashed var(--danger)',
            borderRadius: 'var(--radius)',
          }}
        >
          {refusal}
        </p>
      )}

      {/* Creating a category is not destruction, so it is not gated either (`AUT-01`, `INV-10`). */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          data-testid="care-category-name"
          value={newCategory.name}
          placeholder={de.categoryName}
          onChange={(event) => setNewCategory({ ...newCategory, name: event.target.value })}
          style={{
            flex: '1 1 200px',
            minHeight: 'var(--min-target)',
            fontSize: 'var(--font-body)',
            padding: 8,
            border: '3px solid var(--key-border)',
            borderRadius: 'var(--radius)',
          }}
        />
        <input
          data-testid="care-category-icon"
          value={newCategory.icon}
          placeholder={de.categoryIcon}
          onChange={(event) => setNewCategory({ ...newCategory, icon: event.target.value })}
          style={{
            width: 80,
            minHeight: 'var(--min-target)',
            fontSize: 'var(--font-body)',
            padding: 8,
            border: '3px solid var(--key-border)',
            borderRadius: 'var(--radius)',
          }}
        />
        <input
          data-testid="care-category-colour"
          type="color"
          value={newCategory.colour}
          disabled={newCategory.noColour}
          onChange={(event) => setNewCategory({ ...newCategory, colour: event.target.value })}
          style={{ width: 64, minHeight: 'var(--min-target)' }}
        />
        {/* `<input type="color">` always has a value, so an absence needs its own control (`AUT-11`). The
            picker is disabled while this is ticked, so the two cannot disagree about what will be saved. */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 'var(--min-target)' }}>
          <input
            data-testid="care-category-no-colour"
            type="checkbox"
            checked={newCategory.noColour}
            onChange={(event) => setNewCategory({ ...newCategory, noColour: event.target.checked })}
            style={{ width: 28, height: 28 }}
          />
          Keine Farbe
        </label>
        <CareButton id="care:add-category" label={de.addCategory} onPress={addCategory} />
      </div>

      {/*
        Grouped by category rather than one flat list, because order is per category (`AUT-02`) and a
        run of forty rows gives a caregiver no way to see which board they are arranging.

        Sorted by `order` alone, with pinning shown rather than applied: the board puts pinned tiles
        first (`LRN-09`), so a list that did the same would make the arrows appear to do nothing at
        the boundary between the two groups.
      */}
      {categories.map((category) => (
        <section
          key={category.id}
          data-testid={`care-category:${category.id}`}
          style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
        >
          <header style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {categoryDraft !== null && categoryDraft.id === category.id ? (
              <>
                <input
                  data-testid="care-edit-category-name"
                  value={categoryDraft.name}
                  onChange={(event) =>
                    setCategoryDraft({ ...categoryDraft, name: event.target.value })
                  }
                  style={{
                    flex: '1 1 200px',
                    minHeight: 'var(--min-target)',
                    fontSize: 'var(--font-body)',
                    padding: 8,
                    border: '3px solid var(--key-border)',
                    borderRadius: 'var(--radius)',
                  }}
                />
                <input
                  data-testid="care-edit-category-icon"
                  value={categoryDraft.icon}
                  placeholder={de.categoryIcon}
                  onChange={(event) =>
                    setCategoryDraft({ ...categoryDraft, icon: event.target.value })
                  }
                  style={{
                    width: 80,
                    minHeight: 'var(--min-target)',
                    fontSize: 'var(--font-body)',
                    padding: 8,
                    border: '3px solid var(--key-border)',
                    borderRadius: 'var(--radius)',
                  }}
                />
                <input
                  data-testid="care-edit-category-colour"
                  type="color"
                  value={categoryDraft.colour}
                  disabled={categoryDraft.noColour}
                  onChange={(event) =>
                    setCategoryDraft({ ...categoryDraft, colour: event.target.value })
                  }
                  style={{ width: 64, minHeight: 'var(--min-target)' }}
                />
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 'var(--min-target)' }}
                >
                  <input
                    data-testid="care-edit-category-no-colour"
                    type="checkbox"
                    checked={categoryDraft.noColour}
                    onChange={(event) =>
                      setCategoryDraft({ ...categoryDraft, noColour: event.target.checked })
                    }
                    style={{ width: 28, height: 28 }}
                  />
                  Keine Farbe
                </label>
                <CareButton id="care:save-category" label={de.save} onPress={saveCategoryDraft} />
                <CareButton
                  id="care:cancel-category"
                  label={de.cancel}
                  onPress={() => setCategoryDraft(null)}
                />
              </>
            ) : (
              <>
                <span style={{ flex: '1 1 auto', fontSize: 'var(--font-body)', fontWeight: 700 }}>
                  {category.icon ? `${category.icon} ` : ''}
                  {category.name}
                </span>
                {category.locked ? null : (
                  <>
                    <CareButton
                      id={`care:edit-category:${category.id}`}
                      label={de.edit}
                      onPress={() =>
                        guard('editCategory', () => setCategoryDraft(categoryDraftOf(category)))
                      }
                    />
                    {/*
                      Only an empty one can go: deleting a category with tiles in it would take the
                      tiles with it, which is a way to lose the person's words by tidying. Disabled
                      with a reason rather than absent, so the rule is visible.
                    */}
                    <CareButton
                      id={`care:delete-category:${category.id}`}
                      label={de.delete}
                      danger
                      disabled={inCategory(phrases, category.id).length > 0}
                      {...(inCategory(phrases, category.id).length > 0
                        ? { note: de.categoryNotEmpty }
                        : {})}
                      onPress={() =>
                        guard('deleteCategory', () => {
                          void categoryRepo.remove(category.id).then(reload);
                        })
                      }
                    />
                  </>
                )}
              </>
            )}
            <CareButton
              id={`care:category-up:${category.id}`}
              label={de.moveUp}
              onPress={() =>
                guard('moveCategory', () =>
                  applyMove(categories, category.id, 'up', categoryRepo.upsert),
                )
              }
            />
            <CareButton
              id={`care:category-down:${category.id}`}
              label={de.moveDown}
              onPress={() =>
                guard('moveCategory', () =>
                  applyMove(categories, category.id, 'down', categoryRepo.upsert),
                )
              }
            />
          </header>

          <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {inCategory(phrases, category.id).map((phrase) =>
              draft !== null && draft.id === phrase.id ? (
                <li
                  key={phrase.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
                >
                  <input
                    data-testid="care-edit-text"
                    value={draft.text}
                    onChange={(event) => setDraft({ ...draft, text: event.target.value })}
                    style={{
                      flex: '1 1 240px',
                      minHeight: 'var(--min-target)',
                      fontSize: 'var(--font-body)',
                      padding: 8,
                      border: '3px solid var(--key-border)',
                      borderRadius: 'var(--radius)',
                    }}
                  />
                  <input
                    data-testid="care-edit-label"
                    value={draft.shortLabel}
                    placeholder={de.phraseShortLabel}
                    onChange={(event) => setDraft({ ...draft, shortLabel: event.target.value })}
                    style={{
                      flex: '0 1 160px',
                      minHeight: 'var(--min-target)',
                      fontSize: 'var(--font-body)',
                      padding: 8,
                      border: '3px solid var(--key-border)',
                      borderRadius: 'var(--radius)',
                    }}
                  />
                  <select
                    data-testid="care-edit-category"
                    value={draft.categoryId}
                    onChange={(event) => setDraft({ ...draft, categoryId: event.target.value })}
                    style={{ minHeight: 'var(--min-target)', fontSize: 'var(--font-body)' }}
                  >
                    {categories.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input
                      data-testid="care-edit-pinned"
                      type="checkbox"
                      checked={draft.pinned}
                      onChange={(event) => setDraft({ ...draft, pinned: event.target.checked })}
                    />
                    {de.phrasePinned}
                  </label>
                  <CareButton id="care:save-phrase" label={de.save} onPress={saveDraft} />
                  <CareButton
                    id="care:cancel-phrase"
                    label={de.cancel}
                    onPress={() => {
                      setDraft(null);
                      setRefusal(null);
                    }}
                  />
                </li>
              ) : (
                <li key={phrase.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ flex: '1 1 auto', fontSize: 'var(--font-body)' }}>
                    {phrase.text}
                    {phrase.pinned ? ` ${de.pinnedNote}` : ''}
                  </span>
                  <CareButton
                    id={`care:phrase-up:${phrase.id}`}
                    label={de.moveUp}
                    onPress={() =>
                      guard('movePhrase', () =>
                        applyMove(
                          inCategory(phrases, category.id),
                          phrase.id,
                          'up',
                          phraseRepo.upsert,
                        ),
                      )
                    }
                  />
                  <CareButton
                    id={`care:phrase-down:${phrase.id}`}
                    label={de.moveDown}
                    onPress={() =>
                      guard('movePhrase', () =>
                        applyMove(
                          inCategory(phrases, category.id),
                          phrase.id,
                          'down',
                          phraseRepo.upsert,
                        ),
                      )
                    }
                  />
                  {phrase.locked ? (
                    // The Notfall core set is protected from being edited away (1.10), which here
                    // means no editor either: its wording is on the printed card as well.
                    <span style={{ opacity: 0.7 }}>{de.surfaceNotfall}</span>
                  ) : (
                    <>
                      <CareButton
                        id={`care:edit-phrase:${phrase.id}`}
                        label={de.edit}
                        onPress={() =>
                          guard('editPhrase', () => {
                            setRefusal(null);
                            setDraft(draftOf(phrase));
                          })
                        }
                      />
                      <CareButton
                        id={`care:delete-phrase:${phrase.id}`}
                        label={de.delete}
                        danger
                        onPress={() =>
                          guard('deletePhrase', () => {
                            void phraseRepo.remove(phrase.id).then(reload);
                          })
                        }
                      />
                    </>
                  )}
                </li>
              ),
            )}
          </ul>
        </section>
      ))}
    </section>
  );
}

/** The German for each way a code can be wrong (`AUT-09`). */
const ABBREV_PROBLEM: Record<AbbrevProblem, string> = {
  empty: de.abbrevEmpty,
  notShorter: de.abbrevNotShorter,
  duplicate: de.abbrevDuplicate,
  prefixOfWord: de.abbrevPrefixOfWord,
};

function AbbrevPanel({
  guard,
}: {
  guard: (action: CareAction, run: () => void) => void;
}): React.JSX.Element {
  const [abbrevs, setAbbrevs] = useState<Abbrev[]>([]);
  const [code, setCode] = useState('');
  const [expansion, setExpansion] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const reload = (): void => void abbrevRepo.all().then(setAbbrevs);
  useEffect(reload, []);

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          data-testid="care-abbrev-code"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          placeholder="Code"
          style={{ width: 120, minHeight: 'var(--min-target)', padding: 8 }}
        />
        <input
          data-testid="care-abbrev-expansion"
          value={expansion}
          onChange={(event) => setExpansion(event.target.value)}
          placeholder={de.addAbbrev}
          style={{ flex: '1 1 240px', minHeight: 'var(--min-target)', padding: 8 }}
        />
        <CareButton
          id="care:add-abbrev"
          label={de.addAbbrev}
          onPress={() => {
            const candidate = { code: code.trim().toLowerCase(), expansion: expansion.trim() };

            // AUT-09. Checked against the core vocabulary rather than the packed lexicon, which lives
            // in a worker this thread may not touch.
            const wrong = checkAbbrev(candidate, abbrevs, SEED_KERNVOKABULAR);
            if (wrong !== null) {
              setProblem(ABBREV_PROBLEM[wrong]);
              return;
            }
            setProblem(null);

            void abbrevRepo.upsert(candidate).then(() => {
              setCode('');
              setExpansion('');
              reload();
            });
          }}
        />
      </div>

      {problem === null ? null : (
        <p
          data-testid="care-abbrev-problem"
          style={{
            padding: 8,
            fontSize: 'var(--font-body)',
            border: '3px dashed var(--danger)',
            borderRadius: 'var(--radius)',
          }}
        >
          {problem}
        </p>
      )}
      <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {abbrevs.map((abbrev) => (
          <li key={abbrev.code} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <strong style={{ width: 80 }}>{abbrev.code}</strong>
            <span style={{ flex: '1 1 auto' }}>{abbrev.expansion}</span>
            <CareButton
              id={`care:delete-abbrev:${abbrev.code}`}
              label={de.delete}
              danger
              onPress={() =>
                guard('deleteAbbrev', () => {
                  void abbrevRepo.remove(abbrev.code).then(reload);
                })
              }
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

function AudioPanel(): React.JSX.Element {
  return (
    <>
      <PrerenderPanel />
      <OfflineVoicePanel />
    </>
  );
}

/**
 * The offline voice (2.8).
 *
 * Two deliberate pieces of honesty on this screen. The **90 MB** is in the button label rather than discovered
 * afterwards, and the **accent warning** is stated before the download rather than after — Piper has no Swiss
 * German voice, so when the network drops the person will sound noticeably different, and that is the family's
 * decision to make with the information rather than to discover mid-conversation.
 */
function OfflineVoicePanel(): React.JSX.Element {
  const settings = useProfileStore((s) => s.settings);
  const update = useProfileStore((s) => s.update);
  const assets = usePiperAssets();
  const piper = usePiper();

  useEffect(() => {
    void usePiperAssets.getState().check();
  }, []);

  const percent =
    assets.progress === null
      ? null
      : Math.round((assets.progress.receivedBytes / assets.progress.totalBytes) * 100);

  return (
    <section
      data-testid="care-offline-voice"
      style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 'var(--font-body)' }}
    >
      <h2 style={{ fontWeight: 700 }}>{de.offlineVoiceTitle}</h2>
      <p>{de.offlineVoiceIntro}</p>
      <p style={{ fontWeight: 700 }}>{de.offlineVoiceAccent}</p>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 'var(--min-target)' }}>
        <input
          data-testid="care-piper-enabled"
          type="checkbox"
          checked={settings.piperEnabled}
          onChange={(event) => {
            void update({ piperEnabled: event.target.checked });
            // Starting is idempotent and does nothing while the assets are absent, so this needs no branch.
            if (event.target.checked) void usePiper.getState().start();
          }}
        />
        {de.offlineVoiceEnable}
      </label>

      <p data-testid="care-piper-state">
        {assets.downloaded === null
          ? '…'
          : assets.downloaded
            ? `${de.offlineVoiceReady}${piper.ready ? '' : ' …'}${
                assets.sizeBytes ? ` (${Math.round(assets.sizeBytes / 1_000_000)} MB)` : ''
              }`
            : de.offlineVoiceNotLoaded}
      </p>

      {assets.progress ? (
        <p data-testid="care-piper-progress">
          {de.offlineVoiceDownloading} {percent}%
        </p>
      ) : null}
      {assets.error ? (
        <>
          <p data-testid="care-piper-error">
            {assets.error.kind === 'server' ? de.offlineVoiceFailedServer : de.offlineVoiceFailed}
          </p>
          {/* The raw message. A caregiver cannot fix `failed with 404`, but they can repeat it to somebody who
              can — which is the difference between this evening and next week. */}
          <p
            data-testid="care-piper-error-detail"
            style={{ fontSize: 'var(--font-small)', opacity: 0.7 }}
          >
            {de.offlineVoiceFailedDetail} {assets.error.message}
          </p>
        </>
      ) : null}

      {assets.downloaded === false && !assets.progress ? (
        <CareButton
          id="care-piper:download"
          label={de.offlineVoiceDownload}
          onPress={() => {
            void usePiperAssets
              .getState()
              .download()
              // The worker only exists once there is something for it to load, so starting it here is what turns
              // a finished download into a usable voice without a relaunch.
              .then((ok) => (ok ? usePiper.getState().start() : undefined));
          }}
        />
      ) : null}

      {assets.downloaded === true ? (
        <CareButton
          id="care-piper:remove"
          label={de.offlineVoiceRemove}
          danger
          onPress={() => {
            usePiper.getState().stopWorker();
            void usePiperAssets.getState().remove();
          }}
        />
      ) : null}
    </section>
  );
}

function PrerenderPanel(): React.JSX.Element {
  const settings = useProfileStore((s) => s.settings);
  const update = useProfileStore((s) => s.update);
  const [missing, setMissing] = useState<number | null>(null);
  const [progress, setProgress] = useState<string | null>(null);

  useEffect(() => {
    void missingAudioCount(settings.voiceId).then(setMissing);
    // `keyEchoEnabled` too: switching key echo on adds thirty-odd letters to the work list, and a
    // caregiver who then reads this number must see the truth rather than the count from before they
    // ticked the box.
  }, [settings.voiceId, settings.keyEchoEnabled]);

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <p data-testid="care-audio-missing" style={{ fontSize: 'var(--font-body)' }}>
        {de.audioMissing}: {missing ?? '…'}
      </p>
      <CareButton
        id="care:prerender"
        label={de.prepareAudio}
        onPress={() => {
          const provider = speechService.chain().find((p) => p.id === 'azureTts');
          if (!provider) return;
          void prerenderMissing({
            provider,
            voiceId: settings.voiceId,
            onProgress: ({ done, total }) => setProgress(`${done} / ${total}`),
          }).then(() => {
            setProgress(null);
            void missingAudioCount(settings.voiceId).then(setMissing);
          });
        }}
      />
      {progress ? <p>{progress}</p> : null}
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 'var(--min-target)' }}>
        <input
          data-testid="care-cloud-tts"
          type="checkbox"
          checked={settings.cloudTtsEnabled}
          onChange={(event) => void update({ cloudTtsEnabled: event.target.checked })}
        />
        Cloud-Stimme verwenden
      </label>
    </section>
  );
}

function BackupPanel({
  guard,
}: {
  guard: (action: CareAction, run: () => void) => void;
}): React.JSX.Element {
  const [status, setStatus] = useState<string | null>(null);
  const [localPassphrase, setLocalPassphrase] = useState('');

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <CareButton
        id="care:export"
        label={de.exportBackup}
        onPress={() => {
          void buildExport().then((data) =>
            fileExportProvider.push(serialiseExport(data), {
              schemaVersion: data.schemaVersion,
              createdAt: data.exportedAt,
            }),
          );
        }}
      />
      {/* Gated: import replaces the whole database. It reads as safe and is the most dangerous
          button on this screen. */}
      <CareButton
        id="care:import"
        label={de.importBackup}
        danger
        onPress={() =>
          guard('importBackup', () => {
            void fileExportProvider.pull().then(async (picked) => {
              if (!picked) return;
              try {
                // Through `restoreFrom`, so a local file that happens to be an *encrypted* backup works
                // too — the route the bytes travelled stopped mattering (4.7).
                const text = await picked.blob.text();
                const result = await restoreFrom({ text, passphrase: localPassphrase });
                setStatus(result.kind === 'sealed' ? de.restoreDone : 'OK');
              } catch (err) {
                setStatus(err instanceof Error ? err.message : String(err));
              }
            });
          })
        }
      />
      {/* Only needed if the picked file turns out to be encrypted, and asking beforehand would put a
          password box in front of the ordinary plaintext case. Empty is the normal answer. */}
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--font-body)' }}>
        {de.restorePassphrasePrompt}
        <input
          data-testid="care-import-passphrase"
          type="text"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={localPassphrase}
          onChange={(event) => setLocalPassphrase(event.target.value)}
          style={{ minHeight: 'var(--min-target)', fontSize: 'var(--font-body)', padding: 8 }}
        />
      </label>
      {status ? <p data-testid="care-backup-status">{status}</p> : null}

      {/* The local file above needs no secrets and stays plaintext on purpose. Everything below is for
          the copy that leaves the device, which is why it is encrypted and why the words matter.

          The order is deliberate and is the same ordering 4.7 asks the first-run banner to enforce:
          **restore before setup.** A caregiver holding a card and a replacement iPad should meet the field
          that takes the card before they meet the button that would orphan it (C57). */}
      <SecretsExplainer />
      <AdoptDevicePanel />
      <AutoBackupPanel />
      <CloudRestorePanel guard={guard} />
      <BackupSecrets />
    </section>
  );
}

/**
 * Taking on an existing backup on a device that has never seen it — the replacement iPad (C57).
 *
 * Shown only while this device has no credentials of its own, because that is the only situation in which it
 * is the right thing to do. Everything needed is on the card: the device key authenticates to the endpoint,
 * and the six words are asked for later, at the restore itself.
 *
 * Without this panel the phase did not work. A fresh install had no token, so the restore list said "not
 * configured" and the scenario the whole phase exists for — the iPad is gone, here is a new one — had no path
 * through the app at all.
 */
/**
 * What the three secrets are (`BAK-18`, C86).
 *
 * A tester asked. The PIN, the six Sicherheitswörter and the Geräteschlüssel are unrelated, and the app
 * explained them in two parenthesised phrases on the screen `BAK-02` deliberately shows **once** — so after
 * acknowledgement the vocabulary was never explained again, and `AdoptDevicePanel`, the one other place the
 * Geräteschlüssel is named, returns null the moment credentials exist.
 *
 * Mounted here rather than inside that panel for exactly that reason: this has to be readable on a device that
 * is already set up, which is every device except the one being replaced. Above the restore controls, so a
 * caregiver meets it before the button that would orphan a card (C57).
 *
 * Not collapsible. A disclosure triangle would put the explanation back behind an action, which is the defect.
 */
function SecretsExplainer(): React.JSX.Element {
  return (
    <section
      data-testid="care-secrets-explainer"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: 8,
        fontSize: 'var(--font-body)',
        border: '3px solid var(--key-border)',
        borderRadius: 'var(--radius)',
      }}
    >
      <h3 style={{ fontSize: 'var(--font-body)', fontWeight: 700 }}>{de.secretsExplainerTitle}</h3>
      <p>{de.secretsExplainerPin}</p>
      <p>{de.secretsExplainerWords}</p>
      <p>{de.secretsExplainerToken}</p>
      <p>{de.secretsExplainerWhen}</p>
    </section>
  );
}

function AdoptDevicePanel(): React.JSX.Element | null {
  const [needed, setNeeded] = useState<boolean | null>(null);
  const [adopted, setAdopted] = useState(false);
  const [token, setToken] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    void backupCredentials().then((found) => setNeeded(found === null));
  }, []);

  if (needed !== true) return null;

  const adopt = async (): Promise<void> => {
    if (token.trim() === '') {
      setMessage(de.adoptEmpty);
      return;
    }
    await adoptDeviceToken(token);
    await useBackup.getState().hydrate();
    setMessage(de.adoptDone);
    // The panel stays on screen, showing the confirmation and what to do next. An earlier version flipped
    // `needed` here, which unmounted the whole section the instant it succeeded — so the caregiver tapped
    // «Übernehmen» and watched everything disappear, which reads as a failure rather than a success.
    setAdopted(true);
  };

  return (
    <section
      data-testid="care-adopt"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        fontSize: 'var(--font-body)',
        padding: 8,
        border: '3px solid var(--key-border)',
        borderRadius: 'var(--radius)',
      }}
    >
      <h2 style={{ fontWeight: 700 }}>{de.adoptTitle}</h2>

      {adopted ? null : (
        <>
          <p>{de.adoptIntro}</p>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {de.adoptTokenLabel}
            <input
              data-testid="care-adopt-token"
              type="text"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={token}
              onChange={(event) => setToken(event.target.value)}
              style={{ minHeight: 'var(--min-target)', fontSize: 'var(--font-body)', padding: 8 }}
            />
          </label>
          <CareButton id="care-adopt:save" label={de.adoptSave} onPress={() => void adopt()} />
        </>
      )}

      {message ? <p data-testid="care-adopt-message">{message}</p> : null}
    </section>
  );
}

/**
 * The automatic backup's state, which is the only place it is ever visible.
 *
 * 4.6 makes failure silent on purpose — the person cannot fix an upload and their screen is for speaking — so
 * this panel is the entire feedback channel. That puts a real obligation on it: it has to distinguish "never
 * set up" from "set up and working" from "failing", because a caregiver who cannot tell those apart has no
 * way to know whether the person's vocabulary is protected.
 */
function AutoBackupPanel(): React.JSX.Element {
  const status = useBackup();

  useEffect(() => {
    void useBackup.getState().hydrate();
  }, []);

  const [result, setResult] = useState<string | null>(null);

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 'var(--font-body)' }}>
      <h2 style={{ fontWeight: 700 }}>{de.backupAutoTitle}</h2>

      {shouldEscalate(status) ? (
        <p
          data-testid="care-backup-failing"
          style={{ padding: 8, border: '3px solid var(--danger)', borderRadius: 'var(--radius)' }}
        >
          {de.backupFailing}
        </p>
      ) : null}

      <p data-testid="care-backup-auto-status">
        {!status.configured
          ? de.backupNotConfigured
          : status.lastBackupAt === null
            ? de.backupNever
            : `${de.backupLastSuccess} ${formatWhen(status.lastBackupAt)}`}
        {status.lastError && status.lastBackupAt !== status.lastAttemptAt && status.lastAttemptAt
          ? ` ${de.backupLastAttempt} ${formatWhen(status.lastAttemptAt)} — ${de.backupFailed}`
          : ''}
      </p>

      {status.configured ? (
        <CareButton
          id="care-backup:now"
          label={status.running ? de.backupRunning : de.backupNow}
          onPress={() => {
            setResult(null);
            void useBackup
              .getState()
              .backUpNow()
              .then((ok) => setResult(ok ? de.backupSucceeded : de.backupFailed));
          }}
        />
      ) : null}
      {result ? <p data-testid="care-backup-now-result">{result}</p> : null}
    </section>
  );
}

/**
 * Restore from the cloud: list, choose, confirm.
 *
 * §5.4's principle again — the app proposes, a human decides — applied to the most destructive action in the
 * product. Nothing is fetched until a version is chosen, and nothing is applied until the six words are typed
 * and the gated confirmation is tapped.
 */
function CloudRestorePanel({
  guard,
}: {
  guard: (action: CareAction, run: () => void) => void;
}): React.JSX.Element {
  const [versions, setVersions] = useState<SyncVersion[] | null>(null);
  const [chosen, setChosen] = useState<SyncVersion | null>(null);
  const [passphrase, setPassphrase] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setBusy(true);
    setMessage(null);
    try {
      await azureBlobProvider.refresh();
      if (!azureBlobProvider.isConfigured()) {
        setMessage(de.backupNotConfigured);
        setVersions([]);
        return;
      }
      setVersions(await azureBlobProvider.versions());
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
      setVersions([]);
    } finally {
      setBusy(false);
    }
  };

  const restore = (version: SyncVersion): void => {
    guard('importBackup', () => {
      setBusy(true);
      setMessage(de.restoreWorking);
      void (async () => {
        try {
          const pulled = await azureBlobProvider.pull(version.id === '' ? undefined : version.id);
          if (!pulled) {
            setMessage(de.restoreNoVersions);
            return;
          }
          await restoreFrom({ text: await pulled.blob.text(), passphrase });
          setMessage(de.restoreDone);
          setChosen(null);
        } catch (err) {
          setMessage(err instanceof Error ? err.message : String(err));
        } finally {
          setBusy(false);
        }
      })();
    });
  };

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 'var(--font-body)' }}>
      <h2 style={{ fontWeight: 700 }}>{de.restoreTitle}</h2>
      <CareButton id="care-restore:list" label={de.restoreFromCloud} onPress={() => void load()} />

      {busy && versions === null ? <p>{de.restoreVersionsLoading}</p> : null}
      {message ? <p data-testid="care-restore-message">{message}</p> : null}

      {versions !== null && versions.length === 0 && !message ? (
        <p data-testid="care-restore-none">{de.restoreNoVersions}</p>
      ) : null}

      {versions !== null && versions.length > 0 ? (
        <ul
          data-testid="care-restore-versions"
          style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          {versions.map((version, index) => (
            <li key={version.id || `current-${index}`} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ flex: '1 1 auto' }}>
                {formatWhen(version.createdAt)}
                {version.sizeBytes === null ? '' : ` · ${Math.round(version.sizeBytes / 1024)} KB`}
              </span>
              <CareButton
                id={`care-restore:choose:${index}`}
                label={de.confirm}
                onPress={() => setChosen(version)}
              />
            </li>
          ))}
        </ul>
      ) : null}

      {chosen ? (
        <div
          data-testid="care-restore-confirm"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: 8,
            border: '3px solid var(--danger)',
            borderRadius: 'var(--radius)',
          }}
        >
          <p>{formatWhen(chosen.createdAt)}</p>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {de.restorePassphrasePrompt}
            <input
              data-testid="care-restore-passphrase"
              type="text"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={passphrase}
              onChange={(event) => setPassphrase(event.target.value)}
              style={{ minHeight: 'var(--min-target)', fontSize: 'var(--font-body)', padding: 8 }}
            />
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <CareButton
              id="care-restore:apply"
              label={busy ? de.restoreWorking : de.restoreConfirm}
              danger
              onPress={() => restore(chosen)}
            />
            <CareButton id="care-restore:cancel" label={de.cancel} onPress={() => setChosen(null)} />
          </div>
        </div>
      ) : null}
    </section>
  );
}

/** A timestamp a caregiver can read, in the one format Swiss German readers expect. */
function formatWhen(iso: string): string {
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return iso;
  const date = new Date(parsed);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** How many recent utterances the latency list shows. Enough to see a pattern, short enough to read. */
const LATENCY_ROWS = 8;

function DiagnosticsPanel(): React.JSX.Element {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [latencies, setLatencies] = useState<Array<{ providerId: string; msToAudio: number | null }>>(
    [],
  );
  const [meta, setMeta] = useState<Meta | null>(null);
  const waiting = useUpdateStore((s) => s.waiting);
  const installUpdate = useUpdateStore((s) => s.installUpdate);
  const sessionConfigured = useAudioStatus((s) => s.sessionConfigured);
  const repairs = useAudioStatus((s) => s.repairs);
  const repairFromGesture = useAudioStatus((s) => s.repairFromGesture);
  // Read once on open rather than subscribed: it is a stopwatch, and a panel that re-rendered per frame
  // to tick it would be busywork on the surface a caregiver opens while somebody is waiting to speak.
  const [warmedAgo] = useState(() => warmedAgoMs());

  useEffect(() => {
    void diagnosticsRepo.recent(200).then((entries) => {
      const tally: Record<string, number> = {};
      for (const entry of entries) tally[entry.type] = (tally[entry.type] ?? 0) + 1;
      setCounts(tally);

      // Newest first already. The provider and the tap-to-sound latency are what distinguish a cold
      // backend from a dead context, which C80 records as having been a guess for want of this list.
      setLatencies(
        entries
          .filter((entry) => entry.type === 'UtteranceSpoken')
          .slice(0, LATENCY_ROWS)
          .map((entry) => ({
            providerId: String(entry.payload.providerId ?? '?'),
            msToAudio:
              typeof entry.payload.msToAudio === 'number' ? entry.payload.msToAudio : null,
          })),
      );
    });
    void metaRepo.get().then(setMeta);
  }, []);

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 'var(--font-body)' }}>
      {meta?.persistGranted === false ? (
        <p
          data-testid="care-persist-warning"
          style={{ padding: 8, border: '3px solid var(--danger)', borderRadius: 'var(--radius)' }}
        >
          {de.storageNotPersistent}
        </p>
      ) : null}

      <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 12px' }}>
        <dt>Speicher dauerhaft</dt>
        <dd data-testid="care-persist">{String(meta?.persistGranted)}</dd>
        {Object.entries(counts).map(([type, count]) => (
          <div key={type} style={{ display: 'contents' }}>
            <dt>{type}</dt>
            <dd>{count}</dd>
          </div>
        ))}
      </dl>

      {/* The AudioContext trail. Phase 0 never established what re-locks it, so this is how the
          answer arrives — read it on the device after locking the screen or taking a call.
          `note` marks a replacement, which is usually suspended → suspended and would otherwise
          look like nothing having happened at the moment everything did (C80). */}
      <h2 style={{ fontSize: 'var(--font-body)', fontWeight: 700 }}>AudioContext</h2>
      <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 12px' }}>
        <dt>Ton-Modus</dt>
        <dd data-testid="care-audio-session">{String(sessionConfigured)}</dd>
        <dt>Ton-Reparaturen</dt>
        <dd data-testid="care-audio-repairs">{repairs}</dd>
        <dt>Letzte Aufwärmung</dt>
        <dd data-testid="care-audio-warmup">{warmedAgo === null ? '—' : `vor ${warmedAgo} ms`}</dd>
      </dl>
      <ol data-testid="care-audio-trail" style={{ paddingLeft: 20 }}>
        {audioTransitions().map((transition, index) => (
          <li key={index}>
            {transition.from} → {transition.to} ({transition.visibility}, {transition.atMs} ms)
            {transition.note ? ` · ${transition.note}` : ''}
          </li>
        ))}
      </ol>

      {/* Tap to sound, newest first. A blank is a provider that cannot know — `visualOnly` makes no
          sound, and recording that as 0 ms would make the failures look like the fastest utterances
          the device ever managed. Measured elsewhere: 2.09 s cold, 0.44–0.62 s warm. */}
      <h2 style={{ fontSize: 'var(--font-body)', fontWeight: 700 }}>Bis zum Ton</h2>
      <ol data-testid="care-audio-latencies" style={{ paddingLeft: 20 }}>
        {latencies.map((row, index) => (
          <li key={index}>
            {row.providerId} · {row.msToAudio === null ? '—' : `${row.msToAudio} ms`}
          </li>
        ))}
      </ol>

      {waiting ? (
        <CareButton id="care:update" label={de.installUpdate} onPress={() => void installUpdate()} />
      ) : null}

      {/* The caregiver's route to the same two actions (`SUR-24`). Beside `Update installieren`, which is
          the precedent for a reload living in this panel — a caregiver who came here to fix the sound
          should not have to find a different tab. Unlike the person's control these are always present:
          somebody debugging a device needs them before a failure, not only after one. */}
      <CareButton
        id="care:audio-repair"
        label={de.careAudioRepair}
        onPress={() => void repairFromGesture()}
      />
      <CareButton
        id="care:audio-reload"
        label={de.audioReload}
        onPress={() => {
          // INV-02: a human asked. Never mid-sentence.
          if (speechService.isSpeaking()) return;
          window.location.reload();
        }}
      />
    </section>
  );
}

export { isValidNewPin };
